import { Cartesian2, Cartesian3 } from "cesium";
import { CurveType } from "./interface";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { SimaUtils } from "../../utilities/sima-utils";
export class ArcSegment {
    type = CurveType.ARC;
    _radius;
    _center;
    _normal;
    _startPoint;
    _throughPoint;
    _endPoint;
    _rawPoints;
    constructor() {
        this._rawPoints = [];
        this._radius = 0;
        this._center = Cartesian3.ZERO;
        this._normal = Cartesian3.ZERO;
        this._startPoint = Cartesian3.ZERO;
        this._throughPoint = Cartesian3.ZERO;
        this._endPoint = Cartesian3.ZERO;
    }
    get points() {
        return this._rawPoints;
    }
    get center() {
        return this._center;
    }
    get radius() {
        return this._radius;
    }
    get normal() {
        return this._normal;
    }
    get startPoint() {
        return this._startPoint;
    }
    get endPoint() {
        return this._endPoint;
    }
    get length() {
        let length = 0;
        for (let i = 0; i < this._rawPoints.length - 1; i++) {
            length += Cartesian3.distance(this._rawPoints[i], this._rawPoints[i + 1]);
        }
        return length;
    }
    //区 分	0:小円、1:大円
    get isBigArc() {
        return this.length > Math.PI * this._radius;
    }
    get length_2D() {
        let length = 0;
        for (let i = 0; i < this._rawPoints.length - 1; i++) {
            length += Cartesian2.distance(this._rawPoints[i], this._rawPoints[i + 1]);
        }
        return length;
    }
    dispose() {
        this._rawPoints = [];
        this._radius = 0;
        this._center = Cartesian3.ZERO;
        this._normal = Cartesian3.ZERO;
        this._startPoint = Cartesian3.ZERO;
        this._throughPoint = Cartesian3.ZERO;
        this._endPoint = Cartesian3.ZERO;
    }
    computeArcProperties(p1, p2, p3) {
        const v1 = Cartesian3.subtract(p1, p2, new Cartesian3());
        const v2 = Cartesian3.subtract(p3, p2, new Cartesian3());
        const normal = Cartesian3.cross(v1, v2, new Cartesian3());
        Cartesian3.normalize(normal, normal);
        const v2Ortho = Cartesian3.cross(v2, normal, new Cartesian3());
        Cartesian3.normalize(v2Ortho, v2Ortho);
        const halfV2 = Cartesian3.multiplyByScalar(v2, 0.5, new Cartesian3());
        const fullangle = Cartesian3.angleBetween(v1, v2);
        const halfV1Length = Math.sqrt(v1.x ** 2 + v1.y ** 2 + v1.z ** 2) / 2;
        const halfV2Length = Math.sqrt(halfV2.x ** 2 + halfV2.y ** 2 + halfV2.z ** 2);
        const k = halfV2Length / halfV1Length;
        const angle = Math.atan((1 - k * Math.cos(fullangle)) / (k * Math.sin(fullangle)));
        const bisectorLength = halfV2Length * Math.tan(angle);
        const vecToCenter = Cartesian3.add(halfV2, Cartesian3.multiplyByScalar(v2Ortho, bisectorLength, new Cartesian3()), new Cartesian3());
        const center = Cartesian3.add(p2, vecToCenter, new Cartesian3());
        const radius = Cartesian3.distance(center, p1);
        return { center, radius, normal };
    }
    getPointsInCircleArc(center, radius, start, end, normal, interpolationPitch) {
        const result = [];
        result.push(start);
        let loopPoint = start;
        const dirToLoop = Cartesian3.subtract(loopPoint, center, new Cartesian3());
        while (Cartesian3.distance(loopPoint, end) > interpolationPitch) {
            const dirToNext = new Cartesian3();
            Cartesian3.cross(dirToLoop, normal, dirToNext);
            Cartesian3.normalize(dirToNext, dirToNext);
            const nextPoint = Cartesian3.multiplyByScalar(dirToNext, interpolationPitch, new Cartesian3());
            Cartesian3.add(loopPoint, nextPoint, nextPoint);
            Cartesian3.subtract(nextPoint, center, dirToLoop);
            Cartesian3.normalize(dirToLoop, dirToLoop);
            const adjustedNextPoint = Cartesian3.multiplyByScalar(dirToLoop, radius, new Cartesian3());
            Cartesian3.add(center, adjustedNextPoint, adjustedNextPoint);
            result.push(adjustedNextPoint);
            loopPoint = adjustedNextPoint;
        }
        result.push(end);
        return result;
    }
    generateArcFromThreePoints(start, through, end) {
        const { center, radius, normal } = this.computeArcProperties(start, through, end);
        this._rawPoints = this.getPointsInCircleArc(center, radius, start, end, normal, 1);
        this._radius = radius;
        this._center = center;
        this._normal = normal;
        this._startPoint = start;
        this._throughPoint = through;
        this._endPoint = end;
    }
    generateArcFromCenterAndTwoPoints(start, end, center) {
        const toStart = EarthBrainUtils.getNormalizedDirection(center, start);
        const toEnd = EarthBrainUtils.getNormalizedDirection(center, end);
        const normal = Cartesian3.cross(toEnd, toStart, new Cartesian3());
        Cartesian3.normalize(normal, normal);
        this._radius = Cartesian3.distance(start, center);
        this._center = center;
        this._normal = normal;
        this._startPoint = start;
        this._endPoint = end;
        this._rawPoints = EarthBrainUtils.getPointsInCircleArc(this._center, this._radius, this._startPoint, this._endPoint, this._normal, 0.01);
    }
    generateArcFromRadiusAndTwoPoints(start, end, radius, isLargeCircle, stepSize) {
        const points = SimaUtils.createArcFrom2PointsOnACircle(start, end, radius, isLargeCircle, stepSize);
        const { circleCenter, normal } = SimaUtils.getCircleCenterFrom2PointsOnCircleArc(start, end, radius);
        this._rawPoints = points;
        this._radius = radius;
        this._center = circleCenter;
        this._normal = normal;
        this._startPoint = start;
        this._endPoint = end;
    }
    getCurveData() {
        return {
            type: CurveType.ARC,
            start: this._startPoint,
            through: this._throughPoint,
            end: this._endPoint,
        };
    }
    static fromThreePointArcData({ start, through, end }) {
        const segment = new ArcSegment();
        segment.generateArcFromThreePoints(start, through, end);
        return segment;
    }
    static fromCenterAndTwoPoints({ start, end, center, }) {
        const segment = new ArcSegment();
        segment.generateArcFromCenterAndTwoPoints(start, end, center);
        return segment;
    }
    static fromRadiusAndTwoPoints({ start, end, radius, isLargeCircle, stepSize, }) {
        const segment = new ArcSegment();
        segment.generateArcFromRadiusAndTwoPoints(start, end, radius, isLargeCircle, stepSize);
        return segment;
    }
}
//# sourceMappingURL=arc-segment.js.map